import { useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase, isSupabaseConfigured } from '@/integrations/supabase/client';
import { setPendingAdminPath, isSafeAdminPath } from '@/utils/adminRedirect';
import { AuthContext, AuthContextType } from '@/contexts/AuthContextValue';

// Auth cleanup utility
const cleanupAuthState = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      localStorage.removeItem(key);
    }
  });
  Object.keys(sessionStorage || {}).forEach((key) => {
    if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
      sessionStorage.removeItem(key);
    }
  });
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminReady, setAdminReady] = useState(false);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      // In non-configured environments, disable auth gracefully
      setUser(null);
      setSession(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    // Set up auth state listener first - NEVER use async callbacks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          setTimeout(() => { checkAdminStatus(session.user.id); }, 0);
        } else {
          setIsAdmin(false);
          setAdminReady(true);
        }

        // Intercept SIGNED_OUT inside admin paths to preserve return target
        if (event === 'SIGNED_OUT') {
          try {
            const path = window.location.pathname + (window.location.search || '');
            if (path.startsWith('/admin') && isSafeAdminPath(path)) {
              setPendingAdminPath(path);
            }
          } catch { /* ignore */ }
        }

        setLoading(false);
      }
    );

    // Then check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Defer admin check here too
        setTimeout(() => {
          checkAdminStatus(session.user.id);
        }, 0);
      } else {
        setAdminReady(true);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminStatus = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .eq('role', 'admin')
        .maybeSingle(); // Use maybeSingle instead of single to avoid errors
      
      setIsAdmin(!!data && !error);
      setAdminReady(true);
    } catch (error) {
      console.error('Admin check error:', error);
      setIsAdmin(false);
      setAdminReady(true);
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error: Error | null }> => {
    try {
      if (!isSupabaseConfigured) {
        return { error: new Error('Authentication is disabled: Supabase not configured') };
      }
      cleanupAuthState();
      
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if this fails
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

    if (error) return { error: new Error(error.message) };
      
      // The auth state change will handle navigation automatically
      return { error: null };
    } catch (error) {
    return { error: error instanceof Error ? error : new Error('Unknown auth error') };
    }
  };

  const signUp = async (email: string, password: string): Promise<{ error: Error | null }> => {
    try {
      if (!isSupabaseConfigured) {
        return { error: new Error('Sign up is disabled: Supabase not configured') };
      }
      cleanupAuthState();
      
      const redirectUrl = `${window.location.origin}/`;
      
    const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });
      
    return { error: error ? new Error(error.message) : null };
    } catch (error) {
    return { error: error instanceof Error ? error : new Error('Unknown signup error') };
    }
  };

  const signOut = async () => {
    try {
      if (isSupabaseConfigured) {
        try { await supabase.auth.signOut({ scope: 'global' }); } catch { /* ignore */ }
      }
      cleanupAuthState();
      // Soft navigate instead of full reload preserving SPA performance
      if (window.location.pathname.startsWith('/admin')) {
        try {
          const path = window.location.pathname + (window.location.search || '');
          if (isSafeAdminPath(path)) setPendingAdminPath(path);
        } catch { /* ignore */ }
        window.history.replaceState(null, '', '/');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
  adminReady,
    signIn,
    signUp,
    signOut,
    isAdmin
  };

  return (
  <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};